import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Pagination from "../../../../../../components/Pagination";
import Toast from "../../../../../../components/Toast";
import ButtonLoader from "../../../../../../components/ButtonLoader";
import UserPageLayout from "../../../../components/UserPageLayout";
import { useAppContext } from "../../../../../../context/AppContext";
import { highlightText } from "../../../../../../utils/highlight";
import { encodeId } from "../../../../../../utils/hash";
import { AdminAffiliateDashboardData } from "../../network/api/AdminAffiliateDashboardData";
import { AdminAffiliatePartnersData } from "../../network/api/AdminAffiliatePartnersData";
import { AdminAffiliatePartnerData } from "../../network/api/AdminAffiliatePartnerData";

const defaultToast = {
    visible: false,
    message: "",
    type: "primary",
};

const defaultPageProps = {
    page: 1,
    totalPages: 1,
    totalEntries: 0,
    entryLimit: 10,
    visiblePages: [],
};

const defaultPartnerForm = {
    user_id: "",
    affiliate_percentage_tier1: "",
    affiliate_percentage_tier2: "",
    account_manager_percentage: "",
    account_manager_id: "",
    status: "active",
    trusted: false,
    ensure_partner: true,
};

const pickFirst = (source, keys, fallback = null) => {
    for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }
    return fallback;
};

const safeNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const safeInteger = (value, fallback = 0) => {
    return Math.round(safeNumber(value, fallback));
};

const formatCurrency = (value) => {
    return `$${safeNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const formatCount = (value) => {
    return safeInteger(value).toLocaleString();
};

const normalizePercentageForInput = (value) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return "";
    }

    return parsed <= 1 ? String(parsed * 100) : String(parsed);
};

const displayPercentage = (value) => {
    const normalized = normalizePercentageForInput(value);
    return normalized === "" ? "-" : `${safeNumber(normalized)}%`;
};

const normalizeDashboardData = (rawData) => {
    const source = rawData || {};
    const totals = source?.totals || {};

    return {
        totalPartners: safeInteger(pickFirst(totals, ["total_partners", "partners_total"], pickFirst(source, ["total_partners", "partners_total"]))),
        activePartners: safeInteger(pickFirst(totals, ["active_partners"], pickFirst(source, ["active_partners"]))),
        partnersWithBalance: safeInteger(pickFirst(totals, ["partners_with_balance"], pickFirst(source, ["partners_with_balance"]))),
        usersEligibleForPayout: safeInteger(pickFirst(totals, ["users_eligible_for_payout", "eligible_users_count"], pickFirst(source, ["users_eligible_for_payout", "eligible_users_count"]))),
        currentPartnerBalanceLiability: safeNumber(pickFirst(totals, ["current_partner_balance_liability", "partner_balance_liability"], pickFirst(source, ["current_partner_balance_liability", "partner_balance_liability"]))),
        pendingWithdrawalRequests: safeNumber(pickFirst(totals, ["pending_withdrawal_requests", "pending_withdrawal_amount"], pickFirst(source, ["pending_withdrawal_requests", "pending_withdrawal_amount"]))),
        approvedWithdrawalRequests: safeNumber(pickFirst(totals, ["approved_withdrawal_requests", "approved_withdrawals"], pickFirst(source, ["approved_withdrawal_requests", "approved_withdrawals"]))),
        rejectedWithdrawalRequests: safeNumber(pickFirst(totals, ["rejected_withdrawal_requests", "rejected_withdrawals"], pickFirst(source, ["rejected_withdrawal_requests", "rejected_withdrawals"]))),
        totalCommissionsGenerated: safeNumber(pickFirst(totals, ["total_commissions_generated", "lifetime_commissions"], pickFirst(source, ["total_commissions_generated", "lifetime_commissions"]))),
        eligibleUsers: Array.isArray(pickFirst(source, ["eligible_users"], [])) ? pickFirst(source, ["eligible_users"], []) : [],
    };
};

const extractDashboardPayload = (responseData) => {
    if (!responseData || typeof responseData !== "object") {
        return {};
    }

    if (
        responseData.data &&
        typeof responseData.data === "object" &&
        !Array.isArray(responseData.data)
    ) {
        return responseData.data;
    }

    return responseData;
};

const normalizePartner = (partner) => {
    const admin = partner?.admin || {};

    return {
        id: pickFirst(partner, ["id", "user_id"], pickFirst(admin, ["id"])),
        userId: pickFirst(partner, ["user_id", "id"], pickFirst(admin, ["id"])),
        fullname: pickFirst(partner, ["fullname", "full_name"], pickFirst(admin, ["fullname", "full_name"])),
        displayname: pickFirst(partner, ["displayname", "display_name"], pickFirst(admin, ["displayname", "display_name"])),
        email: pickFirst(partner, ["email"], pickFirst(admin, ["email"])),
        referralCode: pickFirst(partner, ["referral_code", "code"]),
        affiliatePercentageTier1: pickFirst(partner, ["affiliate_percentage_tier1", "tier1_percentage"]),
        affiliatePercentageTier2: pickFirst(partner, ["affiliate_percentage_tier2", "tier2_percentage"]),
        accountManagerPercentage: pickFirst(partner, ["account_manager_percentage"]),
        accountManagerId: pickFirst(partner, ["account_manager_id"]),
        accountManagerName: pickFirst(partner, ["account_manager_name"]),
        balance: safeNumber(pickFirst(partner, ["affiliate_balance", "balance", "current_balance"])),
        directReferralCount: safeInteger(pickFirst(partner, ["direct_referral_count", "direct_referrals", "direct_users", "referrals_count"])),
        pendingWithdrawalTotal: safeNumber(pickFirst(partner, ["pending_withdrawal_total", "pending_withdrawals_total", "pending_withdrawals"])),
        payoutMethodAvailability: pickFirst(partner, ["payout_method_availability", "has_payout_method", "payout_methods_configured"], false),
        lifetimeCommissions: safeNumber(pickFirst(partner, ["lifetime_commissions", "total_commissions_generated"])),
        status: pickFirst(partner, ["status"], "unknown"),
        trusted: Boolean(pickFirst(partner, ["trusted"], false)),
        raw: partner,
    };
};

const getPartnerIdentity = (partner) => {
    return (
        partner?.fullname ||
        partner?.displayname ||
        partner?.email ||
        `User #${partner?.userId || partner?.id || "-"}`
    );
};

const getPartnerStatusClass = (status) => {
    if (status === "active") return "success";
    if (status === "pending") return "warning";
    if (status === "blocked" || status === "inactive") return "danger";
    return "secondary";
};

const renderPayoutAvailability = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === "object" && value !== null) {
        return Object.keys(value).length > 0;
    }

    return Boolean(value);
};

const SummaryWidget = ({ title, value, icon, cardClass = "", iconClass = "", textClass = "" }) => (
    <div className="col-xl-3 col-lg-6 col-md-6 mb-4">
        <div className={`widget-stat card h-100 ${cardClass}`}>
            <div className="card-body p-4">
                <div className="media align-items-center">
                    <span className={`me-3 ${iconClass}`}>
                        <i className={icon}></i>
                    </span>
                    <div className={`media-body ${textClass}`}>
                        <p className="mb-1">{title}</p>
                        <h3 className={textClass || ""}>{value}</h3>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export default function PartnersAdmin() {
    const { setPageTitle, setPageUserID, appData } = useAppContext();

    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [dashboardData, setDashboardData] = useState(normalizeDashboardData(null));
    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [partnerForm, setPartnerForm] = useState(defaultPartnerForm);
    const [showModal, setShowModal] = useState(false);
    const [toastConfig, setToastConfig] = useState(defaultToast);

    const [dashboardFilters, setDashboardFilters] = useState({
        minimum_balance: 100,
        eligible_limit: 5,
    });

    const [pageProps, setPageProps] = useState(defaultPageProps);
    const [pageFilters, setPageFilters] = useState({
        page: 1,
        limit: 10,
        status: "",
        q: "",
        min_balance: "",
        eligible_only: "",
    });

    useEffect(() => {
        setPageUserID(null);
        setPageTitle("Partner Management");
    }, [setPageTitle, setPageUserID]);

    useEffect(() => {
        const fetchDashboard = async () => {
            setDashboardLoading(true);

            const response = await AdminAffiliateDashboardData(dashboardFilters);

            if (response.status === "success") {
                setDashboardData(normalizeDashboardData(extractDashboardPayload(response.data)));
            } else {
                setToastConfig({
                    visible: true,
                    message: response.message || "Failed to load partner dashboard.",
                    type: "danger",
                });
            }

            setDashboardLoading(false);
        };

        fetchDashboard();
    }, [dashboardFilters]);

    useEffect(() => {
        const fetchPartners = async () => {
            setPartnersLoading(true);

            const response = await AdminAffiliatePartnersData(pageFilters);

            if (response.status === "success") {
                const data = response.data || {};
                setPartners((data.data || []).map(normalizePartner));
                setPageProps({
                    page: data.page || 1,
                    totalPages: data.total_pages || 1,
                    totalEntries: data.total_records || 0,
                    entryLimit: data.limit || pageFilters.limit || 10,
                    visiblePages: data.visible_pages || [1],
                });
            } else {
                setToastConfig({
                    visible: true,
                    message: response.message || "Failed to load partners.",
                    type: "danger",
                });
            }

            setPartnersLoading(false);
        };

        fetchPartners();
    }, [pageFilters]);

    const handlePageChange = (page) => {
        if (page < 1 || page > pageProps.totalPages) {
            return;
        }

        setPageFilters((prev) => ({
            ...prev,
            page,
        }));
    };

    const handleListFilterChange = (name, value) => {
        setPageFilters((prev) => ({
            ...prev,
            [name]: value,
            page: 1,
        }));
    };

    const openEditModal = (partner) => {
        setSelectedPartner(partner);
        setPartnerForm({
            user_id: partner?.userId || partner?.id || "",
            affiliate_percentage_tier1: normalizePercentageForInput(partner?.affiliatePercentageTier1),
            affiliate_percentage_tier2: normalizePercentageForInput(partner?.affiliatePercentageTier2),
            account_manager_percentage: normalizePercentageForInput(partner?.accountManagerPercentage),
            account_manager_id: partner?.accountManagerId || "",
            status: partner?.status || "active",
            trusted: Boolean(partner?.trusted),
            ensure_partner: true,
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedPartner(null);
        setPartnerForm(defaultPartnerForm);
    };

    const refreshPartners = async () => {
        const response = await AdminAffiliatePartnersData(pageFilters);
        if (response.status === "success") {
            const data = response.data || {};
            setPartners((data.data || []).map(normalizePartner));
            setPageProps({
                page: data.page || 1,
                totalPages: data.total_pages || 1,
                totalEntries: data.total_records || 0,
                entryLimit: data.limit || pageFilters.limit || 10,
                visiblePages: data.visible_pages || [1],
            });
        }
    };

    const refreshDashboard = async () => {
        const response = await AdminAffiliateDashboardData(dashboardFilters);
        if (response.status === "success") {
            setDashboardData(normalizeDashboardData(extractDashboardPayload(response.data)));
        }
    };

    const handleSave = async () => {
        setSaveLoading(true);

        const payload = {
            user_id: safeNumber(partnerForm.user_id, 0),
            ensure_partner: Boolean(partnerForm.ensure_partner),
            trusted: Boolean(partnerForm.trusted),
            status: partnerForm.status || undefined,
        };

        if (partnerForm.affiliate_percentage_tier1 !== "") {
            payload.affiliate_percentage_tier1 = safeNumber(partnerForm.affiliate_percentage_tier1);
        }
        if (partnerForm.affiliate_percentage_tier2 !== "") {
            payload.affiliate_percentage_tier2 = safeNumber(partnerForm.affiliate_percentage_tier2);
        }
        if (partnerForm.account_manager_percentage !== "") {
            payload.account_manager_percentage = safeNumber(partnerForm.account_manager_percentage);
        }
        if (partnerForm.account_manager_id !== "") {
            payload.account_manager_id = safeNumber(partnerForm.account_manager_id);
        }

        const response = await AdminAffiliatePartnerData(payload);

        if (response.status === "success") {
            setToastConfig({
                visible: true,
                message: response.message || "Partner updated successfully.",
                type: "success",
            });
            await Promise.all([refreshPartners(), refreshDashboard()]);
            closeModal();
        } else {
            setToastConfig({
                visible: true,
                message: response.message || "Failed to update partner.",
                type: "danger",
            });
        }

        setSaveLoading(false);
    };

    const summaryCards = useMemo(() => ([
        {
            title: "Total Partners",
            value: formatCount(dashboardData.totalPartners),
            icon: "la la-users",
            cardClass: "bg-primary",
            textClass: "text-white",
        },
        {
            title: "Active Partners",
            value: formatCount(dashboardData.activePartners),
            icon: "la la-user-check",
            cardClass: "bg-success",
            textClass: "text-white",
        },
        {
            title: "Partners With Balance",
            value: formatCount(dashboardData.partnersWithBalance),
            icon: "fas fa-wallet",
            cardClass: "bg-warning",
            textClass: "text-white",
        },
        {
            title: "Eligible For Payout",
            value: formatCount(dashboardData.usersEligibleForPayout),
            icon: "fas fa-money-check-alt",
            cardClass: "bg-info",
            textClass: "text-white",
        },
    ]), [dashboardData]);

    return (
        <UserPageLayout>
            <div className="content-body">
                <div className="container-fluid">
                    <Toast
                        message={toastConfig.message}
                        type={toastConfig.type}
                        visible={toastConfig.visible}
                        setVisible={(visible) => setToastConfig((prev) => ({ ...prev, visible }))}
                    />

                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card custom-card">
                                <div className="card-body p-4">
                                    <div className="row align-items-center gy-4">
                                        <div className="col-xl-7">
                                            <h3 className="mb-2">Partner Management</h3>
                                            <p className="text-muted mb-0">
                                                Review payout exposure, inspect partner health, and update commission settings from a single admin workspace for {appData?.name || "the portal"}.
                                            </p>
                                        </div>
                                        <div className="col-xl-5">
                                            <div className="row g-3">
                                                <div className="col-sm-6">
                                                    <label className="form-label">Minimum balance</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        className="form-control"
                                                        value={dashboardFilters.minimum_balance}
                                                        onChange={(e) => setDashboardFilters((prev) => ({
                                                            ...prev,
                                                            minimum_balance: safeNumber(e.target.value, 0),
                                                        }))}
                                                    />
                                                </div>
                                                <div className="col-sm-6">
                                                    <label className="form-label">Eligible users preview</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="form-control"
                                                        value={dashboardFilters.eligible_limit}
                                                        onChange={(e) => setDashboardFilters((prev) => ({
                                                            ...prev,
                                                            eligible_limit: safeNumber(e.target.value, 1),
                                                        }))}
                                                    />
                                                </div>
                                            </div>
                                            <p className="small text-muted mt-3 mb-0">
                                                {dashboardLoading ? "Refreshing partner metrics..." : "These controls update the admin dashboard summary above the partner list."}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {summaryCards.map((card) => (
                            <SummaryWidget key={card.title} {...card} />
                        ))}
                    </div>

                    <div className="row">
                        <div className="col-xl-4 mb-4">
                            <div className="card custom-card h-100">
                                <div className="card-header border-0 pb-0">
                                    <div>
                                        <h4 className="card-title mb-1">Payout Exposure</h4>
                                        <p className="mb-0 text-muted small">Amounts currently sitting in the affiliate payout cycle.</p>
                                    </div>
                                </div>
                                <div className="card-body pt-3">
                                    <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                                        <div>
                                            <div className="text-muted small">Current partner balance liability</div>
                                            <div className="h4 mb-0">{formatCurrency(dashboardData.currentPartnerBalanceLiability)}</div>
                                        </div>
                                        <span className="me-3 bgl-danger rounded-circle p-3">
                                            <i className="fas fa-scale-balanced"></i>
                                        </span>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-3">
                                        <div>
                                            <div className="text-muted small">Pending withdrawal amount</div>
                                            <div className="h4 mb-0">{formatCurrency(dashboardData.pendingWithdrawalRequests)}</div>
                                        </div>
                                        <span className="me-3 bgl-warning rounded-circle p-3">
                                            <i className="fas fa-hourglass-half"></i>
                                        </span>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <div className="text-muted small">Lifetime commissions generated</div>
                                            <div className="h4 mb-0">{formatCurrency(dashboardData.totalCommissionsGenerated)}</div>
                                        </div>
                                        <span className="me-3 bgl-primary rounded-circle p-3">
                                            <i className="fas fa-chart-line"></i>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-4 mb-4">
                            <div className="card custom-card h-100">
                                <div className="card-header border-0 pb-0">
                                    <div>
                                        <h4 className="card-title mb-1">Withdrawal Status</h4>
                                        <p className="mb-0 text-muted small">Request counts grouped by final review state.</p>
                                    </div>
                                </div>
                                <div className="card-body pt-3">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="border rounded p-3">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">Approved requests</span>
                                                    <span className="fw-semibold">{formatCurrency(dashboardData.approvedWithdrawalRequests)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="border rounded p-3">
                                                <div className="d-flex justify-content-between">
                                                    <span className="text-muted">Rejected requests</span>
                                                    <span className="fw-semibold">{formatCurrency(dashboardData.rejectedWithdrawalRequests)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="alert alert-light border mb-0">
                                                These withdrawal status figures are displayed as amounts based on the API payload returned by the dashboard endpoint.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-4 mb-4">
                            <div className="card custom-card h-100">
                                <div className="card-header border-0 pb-0">
                                    <div>
                                        <h4 className="card-title mb-1">Eligible Users</h4>
                                        <p className="mb-0 text-muted small">Partners above the current minimum balance threshold.</p>
                                    </div>
                                </div>
                                <div className="card-body pt-3">
                                    {dashboardLoading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-primary mb-3" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="text-muted mb-0">Loading eligible partners...</p>
                                        </div>
                                    ) : dashboardData.eligibleUsers.length === 0 ? (
                                        <div className="text-center py-4">
                                            <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                                            <p className="text-muted mb-0">No partners meet the current threshold.</p>
                                        </div>
                                    ) : (
                                        <div className="list-group list-group-flush">
                                            {dashboardData.eligibleUsers.map((user, index) => {
                                                const normalizedUser = normalizePartner(user);

                                                return (
                                                    <div key={`eligible-user-${normalizedUser.userId || index}`} className="list-group-item px-0">
                                                        <div className="d-flex justify-content-between align-items-start gap-3">
                                                            <div>
                                                                <div className="fw-semibold">{getPartnerIdentity(normalizedUser)}</div>
                                                                <div className="small text-muted">{normalizedUser.referralCode || normalizedUser.email || "No referral code"}</div>
                                                            </div>
                                                            <div className="text-end">
                                                                <div className="fw-semibold">{formatCurrency(normalizedUser.balance)}</div>
                                                                <div className="small text-muted">{formatCount(normalizedUser.directReferralCount)} referrals</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card custom-card">
                                <div className="card-header border-0">
                                    <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center w-100">
                                        <div>
                                            <h4 className="card-title mb-1">Partners</h4>
                                            <p className="mb-0 text-muted small">Search, filter, and manage partner settings using the current affiliate admin endpoints.</p>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={() => {
                                                refreshDashboard();
                                                refreshPartners();
                                            }}
                                        >
                                            <i className="fas fa-rotate-right me-2"></i>
                                            Refresh
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div id="guestTable-all-1_wrapper" className="table-responsive dataTables_wrapper no-footer">
                                        <div className="dataTables_length me-4" id="page_limit">
                                            <label>
                                                Show{" "}
                                                <select
                                                    name="page_limit"
                                                    aria-controls="example3"
                                                    className=""
                                                    value={pageProps.entryLimit || pageFilters.limit}
                                                    onChange={(e) => handleListFilterChange("limit", safeNumber(e.target.value, 10))}
                                                >
                                                    <option value="10">10</option>
                                                    <option value="25">25</option>
                                                    <option value="50">50</option>
                                                    <option value="100">100</option>
                                                </select>{" "}
                                                entries
                                            </label>
                                        </div>
                                        <div className="dataTables_length me-4" id="partner_status_filter">
                                            <label>
                                                Status{" "}
                                                <select
                                                    name="status"
                                                    aria-controls="example3"
                                                    className=""
                                                    value={pageFilters.status}
                                                    onChange={(e) => handleListFilterChange("status", e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                    <option value="active">Active</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="blocked">Blocked</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                            </label>
                                        </div>
                                        <div className="dataTables_length me-4" id="partner_min_balance_filter">
                                            <label>
                                                Min Balance{" "}
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className=""
                                                    value={pageFilters.min_balance}
                                                    onChange={(e) => handleListFilterChange("min_balance", e.target.value)}
                                                />
                                            </label>
                                        </div>
                                        <div className="dataTables_length me-4" id="partner_eligible_filter">
                                            <label>
                                                Eligible{" "}
                                                <select
                                                    name="eligible_only"
                                                    aria-controls="example3"
                                                    className=""
                                                    value={pageFilters.eligible_only}
                                                    onChange={(e) => handleListFilterChange("eligible_only", e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                    <option value="1">Eligible only</option>
                                                </select>
                                            </label>
                                        </div>
                                        <div id="example3_filter" className="dataTables_filter">
                                            <label>
                                                Search:
                                                <input
                                                    type="search"
                                                    className=""
                                                    placeholder=""
                                                    aria-controls="example3"
                                                    value={pageFilters.q}
                                                    onChange={(e) => handleListFilterChange("q", e.target.value)}
                                                />
                                            </label>
                                        </div>
                                        <table className="table table-striped" style={{ minWidth: "1200px" }}>
                                            <thead>
                                                <tr>
                                                    <th>Partner</th>
                                                    <th>Referral</th>
                                                    <th>Commission Setup</th>
                                                    <th>Balance</th>
                                                    <th>Direct Referrals</th>
                                                    <th>Pending Withdrawals</th>
                                                    <th>Payout Method</th>
                                                    <th>Status</th>
                                                    <th className="text-end">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {partnersLoading ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-5">
                                                            <div className="spinner-border text-primary mb-3" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                            <div className="text-muted">Loading partners...</div>
                                                        </td>
                                                    </tr>
                                                ) : partners.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-5">
                                                            <div className="d-flex flex-column align-items-center justify-content-center text-muted">
                                                                <i className="fas fa-users-slash fa-3x mb-3"></i>
                                                                <p className="mb-0">No partners match the current filters.</p>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    partners.map((partner) => (
                                                        <tr key={`partner-${partner.userId || partner.id}`}>
                                                            <td>
                                                                <div className="fw-semibold">
                                                                    <Link to={`/users/${encodeId(partner.userId || partner.id)}/profile`}>
                                                                        {highlightText(getPartnerIdentity(partner), pageFilters.q)}
                                                                    </Link>
                                                                </div>
                                                                <div className="small text-muted">{highlightText(partner.email || "-", pageFilters.q)}</div>
                                                                <div className="small text-muted">User ID: {partner.userId || partner.id}</div>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold">{highlightText(partner.referralCode || "-", pageFilters.q)}</div>
                                                                <div className="small text-muted">Manager: {partner.accountManagerName || partner.accountManagerId || "-"}</div>
                                                            </td>
                                                            <td>
                                                                <div className="small">Tier 1: <span className="fw-semibold">{displayPercentage(partner.affiliatePercentageTier1)}</span></div>
                                                                <div className="small">Tier 2: <span className="fw-semibold">{displayPercentage(partner.affiliatePercentageTier2)}</span></div>
                                                                <div className="small">Manager: <span className="fw-semibold">{displayPercentage(partner.accountManagerPercentage)}</span></div>
                                                            </td>
                                                            <td>
                                                                <div className="fw-semibold">{formatCurrency(partner.balance)}</div>
                                                                <div className="small text-muted">Lifetime: {formatCurrency(partner.lifetimeCommissions)}</div>
                                                            </td>
                                                            <td>{formatCount(partner.directReferralCount)}</td>
                                                            <td>{formatCurrency(partner.pendingWithdrawalTotal)}</td>
                                                            <td>
                                                                {renderPayoutAvailability(partner.payoutMethodAvailability) ? (
                                                                    <span className="badge light badge-success">Configured</span>
                                                                ) : (
                                                                    <span className="badge light badge-secondary">Missing</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column gap-2 align-items-start">
                                                                    <span className={`badge light badge-${getPartnerStatusClass(partner.status)}`}>{partner.status}</span>
                                                                    {partner.trusted ? <span className="badge light badge-info">Trusted</span> : null}
                                                                </div>
                                                            </td>
                                                            <td className="text-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary btn-sm"
                                                                    onClick={() => openEditModal(partner)}
                                                                >
                                                                    Manage
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>

                                        <div className="d-flex justify-content-end mt-3">
                                            <button
                                                type="button"
                                                className="btn btn-light btn-sm"
                                                onClick={() => setPageFilters({
                                                    page: 1,
                                                    limit: 10,
                                                    status: "",
                                                    q: "",
                                                    min_balance: "",
                                                    eligible_only: "",
                                                })}
                                            >
                                                Reset Filters
                                            </button>
                                        </div>

                                        <Pagination pageProps={pageProps} onPageChange={handlePageChange} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showModal && selectedPartner ? (
                <div className="modal show d-block" tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                        <div className="modal-content border-0 shadow">
                            <div className="modal-header">
                                <div>
                                    <h5 className="modal-title mb-1">{getPartnerIdentity(selectedPartner)}</h5>
                                    <div className="small text-muted">
                                        {selectedPartner.email || "-"} | Referral code: {selectedPartner.referralCode || "-"}
                                    </div>
                                </div>
                                <button type="button" className="btn-close" onClick={closeModal}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <div className="border rounded p-3 h-100">
                                            <div className="text-muted small mb-1">Affiliate balance</div>
                                            <div className="h4 mb-0">{formatCurrency(selectedPartner.balance)}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="border rounded p-3 h-100">
                                            <div className="text-muted small mb-1">Pending withdrawals</div>
                                            <div className="h4 mb-0">{formatCurrency(selectedPartner.pendingWithdrawalTotal)}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="border rounded p-3 h-100">
                                            <div className="text-muted small mb-1">Lifetime commissions</div>
                                            <div className="h4 mb-0">{formatCurrency(selectedPartner.lifetimeCommissions)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Tier 1 commission (%)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            value={partnerForm.affiliate_percentage_tier1}
                                            onChange={(e) => setPartnerForm((prev) => ({ ...prev, affiliate_percentage_tier1: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Tier 2 commission (%)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            value={partnerForm.affiliate_percentage_tier2}
                                            onChange={(e) => setPartnerForm((prev) => ({ ...prev, affiliate_percentage_tier2: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Account manager commission (%)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            value={partnerForm.account_manager_percentage}
                                            onChange={(e) => setPartnerForm((prev) => ({ ...prev, account_manager_percentage: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Account manager user ID</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="form-control"
                                            value={partnerForm.account_manager_id}
                                            onChange={(e) => setPartnerForm((prev) => ({ ...prev, account_manager_id: e.target.value }))}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            value={partnerForm.status}
                                            onChange={(e) => setPartnerForm((prev) => ({ ...prev, status: e.target.value }))}
                                        >
                                            <option value="active">Active</option>
                                            <option value="pending">Pending</option>
                                            <option value="blocked">Blocked</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Partner flags</label>
                                        <div className="border rounded px-3 py-2">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="partner-trusted"
                                                    checked={partnerForm.trusted}
                                                    onChange={(e) => setPartnerForm((prev) => ({ ...prev, trusted: e.target.checked }))}
                                                />
                                                <label className="form-check-label" htmlFor="partner-trusted">
                                                    Trusted partner
                                                </label>
                                            </div>
                                            <div className="form-check mt-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    id="partner-ensure"
                                                    checked={partnerForm.ensure_partner}
                                                    onChange={(e) => setPartnerForm((prev) => ({ ...prev, ensure_partner: e.target.checked }))}
                                                />
                                                <label className="form-check-label" htmlFor="partner-ensure">
                                                    Ensure partner record exists
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-light border mt-4 mb-0">
                                    Rates accept either `15` or `0.15`. This form keeps them in percentage-style input so the values are easier to review before saving.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-light" onClick={closeModal}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saveLoading}>
                                    {saveLoading ? <ButtonLoader /> : "Save partner settings"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </UserPageLayout>
    );
}
